<script lang="ts">
  import { cx } from '@emotion/css';
  import CheckboxChecked from '$components/Icons/CheckboxChecked.svelte';
  import CheckboxCheckedInverted from '$components/Icons/CheckboxCheckedInverted.svelte';
  import CheckboxUnchecked from '$components/Icons/CheckboxUnchecked.svelte';
  import { BusinessUnit } from '$lib/careers/types';
  import { ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import HorizontalBuLogo, { Theme } from '$components/Logos/HorizontalBULogo.svelte';

  export let brand: BusinessUnit;
  export let handleChange: (event: Event) => void;
  export let selected = false;
  export let disabled = false;

  let hasFocusOrHover = false;

  $: isInvertedOnActive = [
    BusinessUnit.BLOCK,
    BusinessUnit.SQUARE,
    BusinessUnit.CASHAPP,
    BusinessUnit.SPIRAL
  ].includes(brand);

  $: isMonochromeOnActive = [BusinessUnit.CASHAPP, BusinessUnit.SPIRAL].includes(brand);

  $: active = (selected || hasFocusOrHover) && !disabled && isInvertedOnActive;

  const responsiveVars = new ResponsiveVars()
    .addFluid('chip-height', toFluidVarInput(40, 36))
    .addFluid('logo-height', toFluidVarInput(24, 22))
    .addFluid('chip-gap', toFluidVarInput(12, 12))
    .addFluid('chip-px', toFluidVarInput(16, 12))
    .addFluid('checkmark-size', toFluidVarInput(16, 15))
    .toCSS();
</script>

<button
  class={cx('chip', `chip-${brand}`, responsiveVars, {
    'chip--invert': (selected || hasFocusOrHover) && !disabled
  })}
  role="checkbox"
  aria-checked={selected}
  aria-label={brand}
  on:click={(e) => {
    const isTouchDevice = matchMedia('(hover: none) and (pointer: coarse)').matches;

    if (selected && isTouchDevice) {
      hasFocusOrHover = false;
    }

    handleChange(e);
  }}
  data-value={brand}
  on:mouseover={() => (hasFocusOrHover = true)}
  on:mouseout={() => (hasFocusOrHover = false)}
  on:focus={() => (hasFocusOrHover = true)}
  on:blur={() => (hasFocusOrHover = false)}
  tabindex={0}
  {disabled}
>
  <span class={`logo-outer logo-outer-${brand}`}>
    {#if isInvertedOnActive}
      <div class:hidden={!active}>
        <HorizontalBuLogo
          businessUnit={brand}
          theme={Theme.LIGHT_ON_DARK}
          monochrome={isMonochromeOnActive}
        />
      </div>
    {/if}
    <div class:hidden={active}>
      <HorizontalBuLogo businessUnit={brand} theme={Theme.DARK_ON_LIGHT} />
    </div>
  </span>
  <div class="checkmark">
    {#if selected && isInvertedOnActive}
      <CheckboxCheckedInverted />
    {:else if selected}
      <CheckboxChecked />
    {:else}
      <CheckboxUnchecked />
    {/if}
  </div>
</button>

<style lang="scss">
  @import 'src/scss/media';

  .chip {
    width: 100%;
    height: var(--chip-height);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--chip-gap);
    padding: 0 var(--chip-px);
    border: 1px solid var(--twist-color-gray-100);
    border-radius: 1000px;
    color: var(--twist-color-gray-100);
    background-color: var(--twist-color-gray-0);
    cursor: pointer;
    outline: none;

    @include at('lg') {
      width: auto;
    }

    &:hover {
      @media (hover: hover) and (pointer: fine) {
        .checkmark :global(path) {
          opacity: 40%;
        }
      }
    }

    &:active {
      opacity: 60%;
    }

    &:disabled {
      opacity: 40%;
      cursor: not-allowed;
    }

    &:focus-visible {
      border: 1px solid var(--twist-color-gray-0);
    }

    .logo-outer {
      display: flex;
      align-items: center;
      div {
        height: var(--logo-height);
      }
    }
  }

  .checkmark {
    display: flex;
    width: var(--checkmark-size);
    height: var(--checkmark-size);

    & > :global(svg) {
      overflow: visible;
    }
  }

  .chip-block.chip--invert {
    background-color: var(--twist-color-gray-100);
  }

  .chip-square.chip--invert {
    background-color: #006aff;
    border: 1px solid #006aff;
  }

  .chip-cashapp.chip--invert {
    background-color: #01c846;
    border: 1px solid #01c846;
  }

  .chip-tidal.chip--invert {
    background-color: #0ec7c7;
    border: 1px solid #0ec7c7;
  }

  .chip-spiral.chip--invert {
    background: linear-gradient(90deg, #0000ff 0%, #ff00ff 100%);
    border: 1px solid #ffffff;
  }

  .chip-proto.chip--invert {
    background-color: #ff5b00;
    border: 1px solid #ff5b00;
  }

  .chip-afterpay.chip--invert {
    background-color: #b2fce4;
    border: 1px solid #b2fce4;
  }

  .hidden {
    display: none;
  }
</style>
